import { Component, OnInit, ChangeDetectorRef, DestroyRef, inject, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, filter, take, switchMap, noop } from 'rxjs';

import { Actions, ofActionErrored, ofActionSuccessful, Store } from '@ngxs/store';
import { TsSnackbarService } from 'tsui';
import { Gallery, GalleryComponent, GalleryItem, GalleryState, ImageItem } from 'ng-gallery';
import { PanelComponentBase } from '../panels.types';
import { Image } from 'app/modules/projects/projects.types';
import { CustomObjectState } from '@shared/modules/custom-objects/state/state';
import { CustomObjectActions } from '@shared/modules/custom-objects/state/actions';
import { CustomObjectValue } from '@shared/modules/custom-objects/custom-objects.types';
import { ConfirmService } from '@shared/components/widgets/dialog/confirm/confirm.service';

@Component({
  selector: 'panel-gallery',
  templateUrl: './gallery.component.html',
  styleUrls: ['./gallery.component.scss'],
})
export class PanelImagesComponent extends PanelComponentBase<any> implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private galleryRef = this.gallery.ref('photoGallery');
  private getValueById$: Observable<CustomObjectValue> = inject(Store).select(CustomObjectState.getValueById);

  public galleryImages: GalleryItem[];
  public allowedFormats = ['image/jpeg', 'image/png', 'image/gif'];
  public title = new FormControl('');
  public loading: boolean = false;
  public customObjectValue: CustomObjectValue;

  @ViewChild('photoGallery') photoGallery: GalleryComponent;

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly gallery: Gallery,
    private readonly snackBar: TsSnackbarService,
    private readonly confirmService: ConfirmService,
    private readonly cdRef: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit() {
    this.ui = { ...this.ui, editMode: false };
    this.panelDefMemento = JSON.parse(JSON.stringify(this.panelDef));
    this.fetchCustomObjectValue();
    this.setupActions();
  }

  private setupActions(): void {
    this.actions$
      .pipe(ofActionSuccessful(CustomObjectActions.UploadValueImage), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loading = false;
        this.snackBar.open('Succesfully uploaded image', 'success');
        this.photoGallery.next();
        this.cdRef.detectChanges();
      });

    this.actions$
      .pipe(ofActionErrored(CustomObjectActions.UploadValueImage), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loading = false;
        this.snackBar.open('Failed to upload image', 'error');
        this.cdRef.detectChanges();
      });

    this.actions$
      .pipe(ofActionSuccessful(CustomObjectActions.DeleteValueImage), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loading = false;
        this.snackBar.open('Successfully deleted image', 'success');
        this.cdRef.detectChanges();
      });

    this.actions$
      .pipe(ofActionErrored(CustomObjectActions.DeleteValueImage), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loading = false;
        this.snackBar.open('Failed to delete image', 'error');
        this.cdRef.detectChanges();
      });
  }

  private fetchCustomObjectValue(): void {
    this.getValueById$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((customObjectValue) => !!customObjectValue),
      )
      .subscribe((customObjectValue) => {
        this.customObjectValue = customObjectValue;
        this.loadGallery();
      });
  }

  private loadGallery(): void {
    if (this.customObjectValue) {
      this.galleryImages =
        this.customObjectValue.images
          ?.filter((image: Image) => image.list_image_url && image.original_image_url)
          .map((image: Image) => new ImageItem({ src: image.original_image_url, thumb: image.list_image_url })) ?? [];

      this.galleryRef.load(this.galleryImages);
    }
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];

    if (this.allowedFormats.indexOf(file.type) === -1) {
      this.snackBar.open('Invalid file format. Please upload a JPEG, PNG, or GIF file', 'error');
      return;
    } else {
      this.loading = true;
      this.store.dispatch(new CustomObjectActions.UploadValueImage(this.customObjectValue.id, file));
    }
  }

  deletePhoto(gallery: GalleryComponent): void {
    gallery.galleryRef.state
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        switchMap((galleryState: GalleryState) => {
          const imageToDelete = this.customObjectValue.images[galleryState.currIndex];
          return this.confirmService
            .openConfirmationModal({
              message: `Are you sure you want to delete the image <strong>${imageToDelete.attachment_file_name}</strong>?`,
            })
            .pipe(
              filter((result: boolean) => result),
              switchMap(() => {
                this.loading = true;
                return this.store.dispatch(new CustomObjectActions.DeleteValueImage(imageToDelete.id));
              }),
            );
        }),
      )
      .subscribe(noop);
  }

  saveLayout(): void {
    this.panelDef = {
      ...this.panelDef,
      title: this.title.value,
      meta: this.panelDef.meta,
    };

    this.panelDefUpdated.emit(this.panelDef);
    this.panelDefMemento = JSON.parse(JSON.stringify(this.panelDef));
    this.ui = { ...this.ui, editMode: !this.ui.editMode };
    this.editToggled.emit({ toggle: false, panelDef: this.panelDef });
  }

  toggleEditMode(): void {
    this.ui = { ...this.ui, editMode: !this.ui.editMode };
    this.title.setValue(this.panelDef.title);
    this.editToggled.emit({ toggle: true, panelDef: this.panelDef });
    this.cdRef.detectChanges();
  }

  cancelEdit(): void {
    this.ui = { ...this.ui, editMode: !this.ui.editMode };
    this.panelDef = JSON.parse(JSON.stringify(this.panelDefMemento));
    this.editToggled.emit({ toggle: false, panelDef: this.panelDef });
  }
}
